import { useMemo } from 'react'
import type SHARED_COMPONENTS_EN_US from '../../translations/en-US.json'
import { makeUseMessage, makeUseMessages } from '../../hooks/intl/intl.factories'
import { useI18nContext } from './I18nContext'
import { getLocalizedPartnerDocsUrl } from './intl.utils'

export type MessageIds = keyof typeof SHARED_COMPONENTS_EN_US

export const useMessage = makeUseMessage<MessageIds>()
export const useMessages = makeUseMessages<MessageIds>()

/**
 * Hook that takes an object of documentation paths and returns the same object
 * with localized URLs. Automatically uses the current locale from I18nContext.
 *
 * @example
 * const docsUrls = useLocalizedPartnerDocsUrls({
 *   keywordRedirects: 'merchandising/keyword_redirects/create_keyword_redirect',
 *   gettingStarted: 'getting_started',
 * })
 * // docsUrls.keywordRedirects → 'https://partner-docs.instacart.com/.../es-ES/merchandising/...'
 */
export const useLocalizedPartnerDocsUrls = <T extends Record<string, string>>(paths: T): T => {
  const { locale } = useI18nContext()

  return useMemo(() => {
    const result = {} as T
    for (const key in paths) {
      result[key] = getLocalizedPartnerDocsUrl(paths[key], locale) as T[typeof key]
    }
    return result
  }, [paths, locale])
}
